import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Bell, CalendarDays, Gift, Inbox, Mail, Users, Users2 } from 'lucide-react'
import { Badge, Card, Skeleton } from '@/components/ui/field'
import { alertsQuery } from '@/features/alerts/api'
import { api } from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../_app'

export const Route = createFileRoute('/_app/')({
  component: DashboardPage,
})

interface DashboardStats {
  totalClients: number
  activeClients: number
  pendingReview: number
  sendsThisMonth: number
  birthdayToday: { id: string; fullName: string }[]
  recentRegistrations: { id: string; fullName: string; createdAt: string; source: string }[]
}

function DashboardPage() {
  const { me } = AppRoute.useRouteContext()

  const stats = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: () => api.get<DashboardStats>('/api/dashboard/stats'),
    refetchInterval: 120_000,
  })

  const recentAlerts = useQuery({
    ...alertsQuery({ limit: 5 }),
    enabled: me.permissions.includes('alerts:view'),
  })

  const statCards = [
    {
      label: 'Total clients',
      value: stats.data?.totalClients,
      icon: Users2,
      accent: 'from-blue-500/25 to-blue-600/5',
      border: 'border-blue-500/20',
      iconBg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
      ring: 'hover:border-blue-500/40',
    },
    {
      label: 'Active',
      value: stats.data?.activeClients,
      icon: Users,
      accent: 'from-emerald-500/25 to-emerald-600/5',
      border: 'border-emerald-500/20',
      iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
      ring: 'hover:border-emerald-500/40',
    },
    {
      label: 'Pending review',
      value: stats.data?.pendingReview,
      icon: Inbox,
      accent: 'from-amber-500/25 to-amber-600/5',
      border: 'border-amber-500/20',
      iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
      ring: 'hover:border-amber-500/40',
      link: '/review' as const,
    },
    {
      label: 'Sends this month',
      value: stats.data?.sendsThisMonth,
      icon: Mail,
      accent: 'from-violet-500/25 to-violet-600/5',
      border: 'border-violet-500/20',
      iconBg: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
      ring: 'hover:border-violet-500/40',
    },
  ]

  return (
    <div className="mx-auto max-w-7xl space-y-10">
      <div className="space-y-1.5">
        <h1 className="text-3xl font-semibold tracking-tight">
          Welcome back, {me.user.name.split(' ')[0]}
        </h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Signed in as {me.user.role} · Last sign-in {formatDateTime(me.user.lastLoginAt)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((s) => {
          const content = (
            <div
              className={cn(
                'group relative overflow-hidden rounded-xl border bg-[var(--color-card)] shadow-sm',
                'transition-all duration-300 ease-smooth hover:-translate-y-0.5 hover:shadow-md',
                s.border,
                s.ring,
              )}
            >
              <div
                className={cn(
                  'absolute inset-0 bg-gradient-to-br opacity-40 transition-opacity duration-300 group-hover:opacity-60',
                  s.accent,
                )}
              />
              <div className="relative flex items-start justify-between p-5">
                <div className="space-y-1.5">
                  <p className="text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
                    {s.label}
                  </p>
                  {stats.isLoading ? (
                    <Skeleton className="h-9 w-20" />
                  ) : (
                    <p className="font-heading text-4xl font-semibold tracking-tight">
                      {s.value ?? '—'}
                    </p>
                  )}
                </div>
                <div
                  className={cn(
                    'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
                    s.iconBg,
                  )}
                >
                  <s.icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          )
          return s.link ? (
            <Link key={s.label} to={s.link} className="[&]:block">
              {content}
            </Link>
          ) : (
            <div key={s.label}>{content}</div>
          )
        })}
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 xl:grid-cols-3">
        <Card className="p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/15 text-rose-500">
              <Gift className="h-4 w-4" />
            </div>
            <h2 className="text-sm font-semibold">Birthdays today</h2>
          </div>
          {stats.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : !stats.data?.birthdayToday?.length ? (
            <div className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-6 text-center">
              <CalendarDays className="mx-auto mb-3 h-6 w-6 text-[var(--color-muted-foreground)]" />
              <p className="text-sm text-[var(--color-muted-foreground)]">No birthdays today</p>
            </div>
          ) : (
            <ul className="-mx-2 space-y-0.5">
              {stats.data.birthdayToday.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: c.id }}
                    className="group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-rose-500/5"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-500/10 text-xs">
                      🎂
                    </span>
                    <span className="font-medium underline-offset-2 group-hover:underline">
                      {c.fullName}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15 text-amber-500">
              <Inbox className="h-4 w-4" />
            </div>
            <h2 className="text-sm font-semibold">Recent registrations</h2>
          </div>
          {stats.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-5 w-5/6" />
            </div>
          ) : !stats.data?.recentRegistrations?.length ? (
            <div className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-6 text-center">
              <Users2 className="mx-auto mb-3 h-6 w-6 text-[var(--color-muted-foreground)]" />
              <p className="text-sm text-[var(--color-muted-foreground)]">No registrations yet</p>
            </div>
          ) : (
            <div className="-mx-2 space-y-0.5">
              {stats.data.recentRegistrations.slice(0, 5).map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors hover:bg-[var(--color-muted)]"
                >
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: c.id }}
                    className="font-medium underline-offset-2 hover:underline"
                  >
                    {c.fullName}
                  </Link>
                  <div className="flex items-center gap-2">
                    <Badge variant={c.source === 'self_registration' ? 'success' : 'muted'}>
                      {c.source === 'self_registration' ? 'Self-reg' : 'Staff'}
                    </Badge>
                    <span className="text-xs text-[var(--color-muted-foreground)]">
                      {formatDateTime(c.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {me.permissions.includes('alerts:view') && (
          <Card className="p-6">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-destructive-muted)] text-[var(--color-destructive)]">
                <Bell className="h-4 w-4" />
              </div>
              <h2 className="text-sm font-semibold">Recent alerts</h2>
            </div>
            {recentAlerts.isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-2/3" />
                <Skeleton className="h-5 w-5/6" />
              </div>
            ) : !recentAlerts.data?.items?.length ? (
              <div className="rounded-lg border border-dashed border-[var(--color-border)] px-4 py-6 text-center">
                <Bell className="mx-auto mb-3 h-6 w-6 text-[var(--color-muted-foreground)]" />
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  All clear — no alerts
                </p>
              </div>
            ) : (
              <div className="-mx-2 space-y-0.5">
                {recentAlerts.data.items.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-[var(--color-muted)]"
                  >
                    <div className="min-w-0 truncate">
                      <span className={cn(!a.readAt && 'font-semibold')}>{a.title}</span>
                      {a.description && (
                        <span className="ml-1 text-xs text-[var(--color-muted-foreground)]">
                          {a.description}
                        </span>
                      )}
                    </div>
                    <Badge
                      variant={
                        a.severity === 'critical'
                          ? 'destructive'
                          : a.severity === 'warning'
                            ? 'warning'
                            : 'muted'
                      }
                    >
                      {a.severity}
                    </Badge>
                  </div>
                ))}
                <Link
                  to="/alerts"
                  className="mt-3 flex items-center gap-1 px-3 text-xs font-medium text-[var(--color-primary)] transition-colors hover:text-[var(--color-primary)]/80"
                >
                  View all alerts
                  <span aria-hidden>→</span>
                </Link>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  )
}
